import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowUpRight, BellOff, ChevronRight, Copy, Loader2, Mail, Plus, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LwButton, LwEmpty, LwPageHeader, LwSearchInput, LwSegmented, LwStatusPill, LwTable, LwThemeScope, type LwStatusTone } from '@/components/lw';
import { StoredEmailViewer } from '@/components/admin/notification-management/StoredEmailViewer';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { notificationSettingsService, type StoredEmail } from '@/services/notificationSettingsService';
import { openAppraisalReport } from '@/services/openAppraisalReport';
import { openEncompassAttachment } from '@/services/openEncompassAttachment';
import { getAppraisalDeliveryReport } from '@/services/appraisalDeliveryReportService';
import { resumeAppraisalFeeAlerts, type AppraisalFeeFinding } from '@/services/appraisalFeeComplianceService';
import { getPaymentLinksReport, previewPaymentLinkReminder, sendPaymentLinkReminder, type PaymentLinkReminderInput } from '@/services/appraisalWorkspaceService';
import { LoanNumberCell } from '@/components/encompass/prospects/redesign/primitives/LoanNumberCell';
import { AppraisalFeeHistoryDrawer } from '@/components/encompass/prospects/appraisal/AppraisalFeeHistoryDrawer';
import { SendPaymentReminderDialog } from '@/components/encompass/prospects/appraisal/AppraisalPaymentLinks';
import { StopFeeAlertsDialog } from '@/components/encompass/prospects/appraisal/StopFeeAlertsDialog';
import { appraisalDate } from '@/components/encompass/prospects/appraisal/appraisalDate';
import { feeAuditChain, feeTime } from '@/components/encompass/prospects/appraisal/feeAuditChain';
import { reminderLine } from '@/components/encompass/prospects/appraisal/paymentReminderLine';
import { copyPaymentLink } from '@/components/encompass/prospects/appraisal/paymentLinkAmcs';
import { APPRAISAL_FEE_COLUMN_LABEL, appraisalFeeBasisLabel } from '../../../supabase/functions/_shared/invoice-fee-display-contract';
import { appraisalEligibility, type AppraisalPaymentLinksReport, type AppraisalPaymentLinksReportRow } from '../../../supabase/functions/_shared/appraisal-workspace-contract';
import { AppraisalAuditTracker } from './AppraisalAuditTracker';
import { useAppraisalFeeCompliance } from './AppraisalFeeCompliance';
import { ReportAddPaymentLinkModal } from './ReportAddPaymentLinkModal';
import { ReportDateRangePicker } from './date-range/ReportDateRangePicker';
import { makeCustom, type ReportPeriod } from './date-range/reportPeriod';
import { borrowerDeliveryViewFor, groupAppraisalRowsByLoan, APPRAISAL_PIPELINE_LABELS, type AppraisalDeliveryPipeline, type AppraisalDeliveryReportRow, type AppraisalLifecycleState } from './appraisalDeliveryModel';
import './appraisal-fee-compliance.css';

type GridView = 'deliveries' | 'ordering' | 'fees';
type AppraisalView = GridView | 'payments';
type Segment = 'needs' | 'flight' | 'done' | 'excluded';
/** The Payment links tab follows the capture and reminder flag; the report RPC also returns nothing while it is off. */
const PAYMENT_LINKS_FLAG = 'appraisal_payment_links_v1';
const SEGMENTS: Segment[] = ['needs', 'flight', 'done', 'excluded'];
const LABELS = { needs: 'Needs action', flight: 'In flight', done: 'Cleared', excluded: 'Excluded' };
// Fee rows have no cleared state; a loan whose alerts were stopped keeps its finding visible in its own segment.
const FEE_LABELS = { ...LABELS, excluded: 'Alerts stopped' };
const FEE_SEGMENTS: Segment[] = ['needs', 'flight', 'excluded'];
const TONES: Record<Segment, LwStatusTone> = { needs: 'destructive', flight: 'info', done: 'success', excluded: 'neutral' };
const ORDER_STATES: Record<AppraisalLifecycleState, Segment> = { needs_action: 'needs', in_flight: 'flight', delivered: 'done', excluded: 'excluded' };
const DELIVERY_STATES = { needs_review: 'needs', processing: 'flight', delivered: 'done', skipped: 'excluded' } as const;
const PAGE_SIZE = 25;
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const amount = (cents: number | null | undefined) => (cents === null || cents === undefined) ? 'Not available' : money.format(cents / 100);
const COLUMNS: Record<GridView, { label: string; title: string; unit: string; columns: Array<{ label: string; right?: boolean }>; table: string }> = {
  deliveries: { label: 'Borrower deliveries', title: 'Document', unit: 'documents', columns: [{ label: 'Type' }, { label: 'Size', right: true }, { label: 'Mode' }], table: 'Borrower delivery activity' },
  ordering: { label: 'CDA ordering', title: 'Appraisal folder', unit: 'loans', columns: [{ label: 'Files', right: true }, { label: 'Breakdown' }], table: 'CDA ordering activity' },
  fees: { label: 'Fee compliance', title: 'Invoice', unit: 'findings', columns: [{ label: 'On invoice', right: true }, { label: APPRAISAL_FEE_COLUMN_LABEL, right: true }, { label: 'Excess', right: true }], table: 'Appraisal fee discrepancies' }
};
interface GridRow {
  id: string; loanNumber: string; borrowerName: string | null; title: string; sub: string;
  state: Segment; badge?: string; tone?: LwStatusTone; why: string; followUp?: ReactNode;
  values: ReactNode[]; updated: ReactNode; details: ReactNode; actions: ReactNode;
  fee?: AppraisalFeeFinding;
}
const NOTIFICATION_TYPES: Record<AppraisalDeliveryPipeline, string> = {
  cda_order: 'appraisal_cda_order_required',
  cda_delivery: 'cda_delivery_notification',
  transferred: 'transferred_appraisal_delivery',
  avm: 'avm_delivery_notification'
};

const DELIVERY_MODE_LABELS: Record<AppraisalDeliveryReportRow['deliveryMode'], string> = {
  attachment: 'Attached to the email',
  link: 'Secure download link',
  reminder: 'Reminder email (no document)',
  not_sent: 'Not sent'
};

function initialPeriod(): ReportPeriod {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  return { ...makeCustom(from, to), label: 'Last 30 days' };
}

function formatDateTime(value: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  }).format(date);
}

/** Compact relative time. The absolute value rides along in `title`. */
function formatRelative(value: string | null): string {
  if (!value) {
    return '—';
  }
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) {
    return '—';
  }
  const minutes = Math.round((Date.now() - parsed) / 60_000);
  if (minutes < 1) {
    return 'just now';
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.round(hours / 24);
  if (days < 30) {
    return `${days}d ago`;
  }
  return formatDateTime(value);
}

function formatBytes(bytes: number | null): string {
  if (!bytes || bytes < 0) {
    return 'Size unavailable';
  }
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 1 : 2)} MiB`;
}

function technicalReasonFor(row: AppraisalDeliveryReportRow): string {
  const reason = row.skipReason || row.reason;
  if (
    reason.startsWith('canonical_family_already_sent')
    || reason === 'negative_markers'
    || reason === 'insufficient_markers'
    || reason === 'ineligible_loan_state'
  ) {
    return borrowerDeliveryViewFor(row).why;
  }
  return reason || '—';
}

export function AppraisalDeliveryReport({ initialView = 'deliveries' }: { initialView?: AppraisalView }) {
  const navigate = useNavigate();
  const { isAdmin } = useRoleAccess();
  const fees = useAppraisalFeeCompliance();
  const paymentLinks = useFeatureFlag(PAYMENT_LINKS_FLAG);
  const { report: paymentReport, error: paymentsError, loading: paymentsLoading, load: loadPayments } = usePaymentLinksReport();
  const [rows, setRows] = useState<AppraisalDeliveryReportRow[]>([]);
  const [truncated, setTruncated] = useState<AppraisalDeliveryPipeline[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<AppraisalView>(initialView);
  const [filters, setFilters] = useState<Record<AppraisalView, { search: string; segments: Segment[]; page: number }>>({
    deliveries: { search: '', segments: [], page: 0 }, ordering: { search: '', segments: [], page: 0 }, fees: { search: '', segments: [], page: 0 },
    payments: { search: '', segments: [], page: 0 }
  });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [period, setPeriod] = useState<ReportPeriod>(initialPeriod);
  const [storedEmail, setStoredEmail] = useState<StoredEmail | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [emailLoadingRowId, setEmailLoadingRowId] = useState<string | null>(null);
  const [openingFiles, setOpeningFiles] = useState<string[]>([]);
  const [stopFor, setStopFor] = useState<AppraisalFeeFinding | null>(null);
  const [resumingLoans, setResumingLoans] = useState<string[]>([]);
  const resumeAlerts = async (row: AppraisalFeeFinding) => {
    const loanId = row.loanId || '';
    if (!loanId || resumingLoans.includes(loanId)) {
      return;
    }
    setResumingLoans(previous => [...previous, loanId]);
    try {
      await resumeAppraisalFeeAlerts(loanId);
      toast.success(`Alerts resumed for ${row.loanNumber}.`, { position: 'top-right' });
      await fees.refreshReport();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Alerts could not be resumed.', { position: 'top-right' });
    } finally {
      setResumingLoans(previous => previous.filter(value => value !== loanId));
    }
  };
  const openFile = async (id: string, attachmentId: string | undefined, kind: 'invoice' | 'document', encompassLoanId?: string) => {
    const opened = openEncompassAttachment(attachmentId, kind, encompassLoanId);
    setOpeningFiles(previous => [...previous, id]);
    try {
      await opened;
    } finally {
      setOpeningFiles(previous => previous.filter(value => value !== id));
    }
  };
  const openInvoice = (row: AppraisalFeeFinding) => openFile(row.id, row.attachmentId, 'invoice');
  const [openingAppraisals, setOpeningAppraisals] = useState<string[]>([]);
  const openAppraisal = async (row: AppraisalFeeFinding) => {
    if (!row.appraisalReportId || openingAppraisals.includes(row.id)) {
      return;
    }
    const opened = openAppraisalReport({ loanId: row.loanId || '', encompassLoanId: row.encompassLoanId || '' }, row.appraisalReportId);
    setOpeningAppraisals(previous => [...previous, row.id]);
    try {
      await opened;
    } finally {
      setOpeningAppraisals(previous => previous.filter(id => id !== row.id));
    }
  };
  const generation = useRef(0);
  const loadRows = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    setLoadError(null);
    try {
      const result = await getAppraisalDeliveryReport({ from: period.from, to: period.to });
      if (generation.current === current) {
        setRows(result.rows);
        setTruncated(result.truncatedPipelines);
      }
    } catch (error) {
      if (generation.current === current) {
        setRows([]);
        setTruncated([]);
        setLoadError(error instanceof Error ? error.message : 'Appraisal delivery activity could not be loaded.');
      }
    } finally {
      if (generation.current === current) {
        setLoading(false);
      }
    }
  }, [period.from, period.to]);
  useEffect(() => {
    void loadRows();
    const counter = generation;
    return () => {
      counter.current++;
    };
  }, [loadRows]);
  // Payment links load the first time their tab is opened, then on Refresh.
  const paymentsRequested = useRef(false);
  useEffect(() => {
    if (view === 'payments' && !paymentsRequested.current) {
      paymentsRequested.current = true;
      void loadPayments();
    }
  }, [view, loadPayments]);
  const openStoredEmail = async (row: AppraisalDeliveryReportRow) => {
    if (!row.resendId) {
      return;
    }
    setEmailLoadingRowId(row.id);
    try {
      const notificationType = NOTIFICATION_TYPES[row.pipeline];
      const activity = await notificationSettingsService.getActivityPage(notificationType, {
        q: row.loanNumber === 'Restricted' ? row.encompassLoanId : row.loanNumber,
        dateRange: 'all',
        pageSize: 100,
        includeTests: true
      });
      const attempt = activity.rows.find(candidate => candidate.resendId === row.resendId);
      if (!attempt?.hasStoredHtml) {
        throw new Error('The stored email body is not available for this send.');
      }
      setStoredEmail(await notificationSettingsService.getStoredEmail(attempt.id, notificationType));
      setViewerOpen(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The stored email could not be opened.');
    } finally {
      setEmailLoadingRowId(null);
    }
  };

  const openLoan = (loanId: string | null) => {
    if (!loanId) {
      return;
    }
    navigate(`/internal?section=loan-center&loan=${encodeURIComponent(loanId)}`);
  };


  const updateFilter = (patch: Partial<(typeof filters)[AppraisalView]>) => setFilters(previous => ({ ...previous, [view]: { ...previous[view], page: 0, ...patch } }));
  const toggle = (id: string) => setExpanded(previous => {
    const next = new Set(previous);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    return next;
  });
  const groups = useMemo(() => groupAppraisalRowsByLoan(rows.filter(row => row.pipeline === 'cda_order')), [rows]);
  const deliveryRows: GridRow[] = rows.filter(row => row.pipeline !== 'cda_order').map(row => {
    const delivery = borrowerDeliveryViewFor(row);
    return {
      id: row.id, loanNumber: row.loanNumber, borrowerName: row.borrowerName, title: row.documentTitle,
      sub: row.isRotatedCopy ? 'System-generated rotated copy' : APPRAISAL_PIPELINE_LABELS[row.pipeline],
      state: DELIVERY_STATES[delivery.result], why: delivery.why,
      followUp: <>{delivery.followUp}{delivery.result === 'needs_review' && <><p>{row.guidance}</p><p>{row.exceptionNotifiedAt ? `Alert emailed ${formatDateTime(row.exceptionNotifiedAt)}` : 'Alert email pending'}</p></>}</>,
      values: [APPRAISAL_PIPELINE_LABELS[row.pipeline], formatBytes(row.fileSize), DELIVERY_MODE_LABELS[row.deliveryMode]],
      updated: <time title={formatDateTime(row.completedAt)}>{formatRelative(row.completedAt)}</time>,
      details: <>
        <DetailGroup title="Dates" values={[
          ['Uploaded', formatDateTime(row.uploadedAt)], ['Queued', formatDateTime(row.queuedAt)],
          ['Scheduled', formatDateTime(row.scheduledFor)], ['Completed', formatDateTime(row.completedAt)]
        ]} />
        <DetailGroup title="Recipients" values={[
          ['To', row.toEmails.join(', ') || '—'], ['CC', row.ccEmails.join(', ') || '—'],
          ...(row.deliveryLink ? [['Link expires', formatDateTime(row.deliveryLink.expiresAt)],
          ['Link accesses', `${row.deliveryLink.downloadCount}${row.deliveryLink.revokedAt ? ' · revoked' : ''}`],
          ['Access note', 'Access counts can include automated mail-scanner visits.']] : [])
        ]} />
        <DetailGroup title="Encompass follow-up" values={[
          ['Delivery receipt', row.receiptAttachmentId ? 'Filed' : row.sentAt ? 'Not filed yet' : 'Not needed'],
          ['Delivery date', row.pipeline === 'transferred' ? 'Not part of this delivery' : row.field3858WrittenAt ? `Recorded ${formatDateTime(row.field3858WrittenAt)}` : row.sentAt ? 'Not recorded yet' : 'Not needed']
        ]} />
        <DetailGroup title="Technical" values={[
          ['Raw status', row.status], ['Raw reason', technicalReasonFor(row)], ['Resend id', row.resendId || '—'],
          ['Retry counters', `OCR ${row.ocrRetries} · receipt ${row.receiptRetries} · field 3858 ${row.field3858Retries}`],
          ['Last updated', row.lastUpdatedAt ? formatDateTime(row.lastUpdatedAt) : 'Not tracked for this queue']
        ]} />
        {row.collapsedRotationEvidence.length > 0 && <DetailGroup title="Related copies" values={row.collapsedRotationEvidence.map(copy => [copy.documentTitle, formatDateTime(copy.queuedAt)])} />}
      </>,
      actions: <>
        {row.loanId ? <LwButton variant="sm-solid" onClick={() => openLoan(row.loanId)}>Open loan</LwButton> : <span>This loan is outside your access.</span>}
        <LwButton variant="sm" aria-label={`Open document for ${row.loanNumber}`} aria-busy={openingFiles.includes(row.id)} onClick={() => void openFile(row.id, row.attachmentId, 'document', row.encompassLoanId)}><ActionIcon busy={openingFiles.includes(row.id)} />Open document</LwButton>
        {isAdmin && row.resendId && <LwButton variant="sm" aria-busy={emailLoadingRowId === row.id} disabled={emailLoadingRowId === row.id} onClick={() => void openStoredEmail(row)}><ActionIcon busy={emailLoadingRowId === row.id} icon="email" />View email</LwButton>}
      </>
    };
  });
  const orderingRows: GridRow[] = groups.map(group => {
    const reminder = group.documents.filter(document => document.sentAt).sort((a, b) => Date.parse(b.sentAt!) - Date.parse(a.sentAt!))[0];
    return {
      id: group.encompassLoanId, loanNumber: group.loanNumber, borrowerName: group.borrowerName,
      title: 'Appraisal folder', sub: `${group.documents.length} files in the Appraisal folder`,
      state: ORDER_STATES[group.state], badge: group.state === 'delivered' ? 'Ordered' : undefined,
      why: group.documents.find(document => document.state === group.state)?.headline || '',
      values: [group.documents.length, Object.entries(group.counts).filter(([, count]) => count > 0).map(([state, count]) => `${count} ${LABELS[ORDER_STATES[state as AppraisalLifecycleState]].toLowerCase()}`).join(' · ')],
      updated: <time title={formatDateTime(group.latestQueuedAt)}>{formatRelative(group.latestQueuedAt)}</time>,
      details: <>
        <section className="appraisal-detail-group appraisal-folder-detail"><h3>Folder</h3><ul>{group.documents.map(document => <li key={document.id}>
          <span>{document.documentTitle}</span><LwStatusPill label={document.state === 'delivered' ? 'Ordered' : LABELS[ORDER_STATES[document.state]]} tone={TONES[ORDER_STATES[document.state]]} />
          <p>{document.headline}{document.guidance && ` ${document.guidance}`}</p>
        </li>)}</ul></section>
        {reminder && <DetailGroup title="Reminder" values={[
          ['Last sent', formatDateTime(reminder.sentAt)], ...(reminder.toEmails.length ? [['Processor', reminder.toEmails.join(', ')]] : [])
        ]} />}
      </>,
      actions: group.loanId ? <LwButton variant="sm-solid" onClick={() => openLoan(group.loanId)}>Open loan</LwButton> : <span>This loan is outside your access.</span>
    };
  });
  const feeRows: GridRow[] = (fees.report?.rows || []).map(row => {
    const chain = feeAuditChain(row);
    const submitting = fees.submitting.some(item => item.findingId === row.id);
    const stopped = Boolean(row.alertsStoppedAt);
    const resuming = resumingLoans.includes(row.loanId || '');
    return {
      id: row.id, loanNumber: row.loanNumber, borrowerName: row.borrowerName, title: row.documentTitle,
      sub: [row.invoiceFolder, row.vendor].filter(Boolean).join(' · '),
      state: row.pending || row.retrying || row.outcome === 'waiting' ? 'flight' : stopped ? 'excluded' : 'needs',
      badge: chain.busy ? chain.summary : row.outcome === 'overage' ? 'Fee overage' : row.outcome === 'waiting' ? 'Awaiting disclosure' : 'Needs review',
      why: row.reason || 'The invoice could not be matched confidently.', fee: row,
      tone: row.pending || row.retrying || row.outcome === 'waiting' ? 'info' : row.outcome === 'review' ? 'warning' : 'destructive',
      values: [amount(row.invoiceCents), amount(row.feeCents), row.outcome === 'overage' ? amount(row.excessCents) : 'Not determined'],
      followUp: stopped ? <p data-fee-alerts="stopped">Alerts stopped {feeTime(row.alertsStoppedAt)} by {row.alertsStoppedBy || 'a user'}: {row.alertsStoppedReason}</p> : undefined,
      updated: <><span data-fee-checked>{chain.busy ? chain.summary : feeTime(row.checkedAt || row.evaluatedAt)}</span><LwButton variant="sm" className="fee-history-link" onClick={() => fees.setHistoryFor(row)} aria-label={`Audit history for ${row.loanNumber}, ${row.documentTitle}`}>History</LwButton></>,
      details: <>
        <DetailGroup title="Amounts" values={[
          ['Amount on invoice', amount(row.invoiceCents)], [appraisalFeeBasisLabel(row.ruleVersion, row.feeSource), amount(row.feeCents)],
          ['Current fee (reference)', amount(row.currentFeeCents)],
          ...(row.feeSource === 'CD' ? [['Last disclosed LE (reference)', amount(row.lastDisclosedLECents)]] : []),
          ['Confirmed excess', row.outcome === 'overage' ? amount(row.excessCents) : 'Not determined'],
          ['Cure required', amount(row.cures?.requiredCents)], ['Cure applied', amount(row.cures?.appliedCents)]
        ]} />
        <DetailGroup title="Audit" values={[
          ['Checked', feeTime(row.checkedAt || row.evaluatedAt)], ['Rule', row.ruleVersion?.match(/v(\d+)$/)?.[0] || 'Not available'],
          ['Order number', row.orderNumber || 'Not available'],
          ...(row.associationStatus !== 'matched' && row.associationReason ? [['Association', row.associationReason]] : [])
        ]} />
        {stopped && <DetailGroup title="Alerts" values={[
          ['Stopped', feeTime(row.alertsStoppedAt)], ['By', row.alertsStoppedBy || 'Unknown user'], ['Reason', row.alertsStoppedReason || '—']
        ]} />}
      </>,
      actions: <>
        <LwButton variant="sm-solid" data-fee-open onClick={() => void openAppraisal(row)} aria-disabled={!row.appraisalReportId} title={!row.appraisalReportId ? 'No appraisal report on this loan yet' : undefined} aria-busy={openingAppraisals.includes(row.id)} aria-label={`Open appraisal for ${row.loanNumber}`}><ActionIcon busy={openingAppraisals.includes(row.id)} />Open appraisal</LwButton>
        <LwButton variant="sm" aria-label={`Open invoice for ${row.loanNumber}`} aria-busy={openingFiles.includes(row.id)} onClick={() => void openInvoice(row)}><ActionIcon busy={openingFiles.includes(row.id)} />Open invoice</LwButton>
        <LwButton variant="sm" onClick={() => void fees.rerun(row)} aria-disabled={fees.rerunInProgress(row)} title={fees.rerunInProgress(row) ? 'Rerun in progress' : undefined} aria-busy={submitting} aria-label={`Rerun invoice audit for ${row.loanNumber}`}><ActionIcon busy={submitting} icon="rerun" />Rerun audit</LwButton>
        {stopped
          ? <LwButton variant="sm" onClick={() => void resumeAlerts(row)} aria-disabled={resuming} aria-busy={resuming} aria-label={`Resume alerts for ${row.loanNumber}`}><ActionIcon busy={resuming} icon="bell" />Resume alerts</LwButton>
          : <LwButton variant="sm" onClick={() => setStopFor(row)} aria-label={`Stop alerts for ${row.loanNumber}`}><ActionIcon icon="bell" />Stop alerts</LwButton>}
        <div data-fee-action-status>{row.needsAttention ? 'Needs attention' : row.retrying ? 'Retrying' : row.pending && row.freshState && row.freshState !== 'none' ? 'Rerun in progress' : ''}</div>
      </>
    };
  });
  const allRows = { deliveries: deliveryRows, ordering: orderingRows, fees: feeRows };
  const tabs: Array<{ value: AppraisalView; label: string; count?: number; tabId: string; panelId: string }> = [
    ...(Object.keys(COLUMNS) as GridView[]).map(tab => ({
      value: tab, label: COLUMNS[tab].label, count: allRows[tab].filter(row => row.state === 'needs').length,
      tabId: `appraisal-tab-${tab}`, panelId: `appraisal-panel-${tab}`
    })),
    // No count: the other badges mean "needs action", and a captured link does not.
    ...(paymentLinks.enabled ? [{ value: 'payments' as const, label: 'Payment links', tabId: 'appraisal-tab-payments', panelId: 'appraisal-panel-payments' }] : [])
  ];
  const gridView: GridView = view === 'payments' ? 'deliveries' : view;
  const current = filters[gridView];
  const config = COLUMNS[gridView];
  const searched = allRows[gridView].filter(row => `${row.loanNumber} ${row.borrowerName} ${row.title}`.toLowerCase().includes(current.search.trim().toLowerCase()));
  const filtered = searched.filter(row => !current.segments.length || current.segments.includes(row.state))
    .sort((a, b) => view === 'fees' ? a.loanNumber.localeCompare(b.loanNumber) : SEGMENTS.indexOf(a.state) - SEGMENTS.indexOf(b.state));
  const segmentLabels = view === 'fees' ? FEE_LABELS : LABELS;
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const page = Math.min(current.page, pageCount - 1);
  const shown = view === 'fees' ? filtered : filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const excess = filtered.reduce((sum, row) => sum + (row.fee?.outcome === 'overage' ? row.fee.excessCents || 0 : 0), 0);
  const busy = view === 'payments' ? paymentsLoading : view === 'fees' ? !fees.report && !fees.error : loading;
  const error = view === 'fees' ? !fees.report && fees.error : loadError;
  const historyRow = fees.historyFor && (fees.report?.rows.find(row => (row.attachmentId && row.attachmentId === fees.historyFor?.attachmentId) || row.id === fees.historyFor?.id) || fees.historyFor);
  return (
    <LwThemeScope className="lw-ds-page appraisal-processes">
      <div className="appraisal-processes-wrap" ref={fees.reportRoot} tabIndex={-1}>
        {/* Payment links keeps its Refresh on the table's toolbar, beside Add payment link. */}
        <LwPageHeader eyebrow="Company Reports" title={<>Appraisal <em>Processes</em></>} actions={view === 'payments' ? undefined : <div className="appraisal-report-header-actions">
          {(view === 'deliveries' || view === 'ordering') && <><span>Completed between</span><ReportDateRangePicker value={period} onChange={value => {
            setPeriod(value); setFilters(previous => ({ ...previous, deliveries: { ...previous.deliveries, page: 0 }, ordering: { ...previous.ordering, page: 0 } }));
          }} max={new Date()} /></>}
          <LwButton variant="sm" onClick={() => void (view === 'fees' ? fees.refreshReport() : loadRows())} aria-busy={busy}><ActionIcon busy={busy} icon="rerun" />Refresh</LwButton>
        </div>} />
        <LwSegmented mode="tabs" ariaLabel="Appraisal workflow" value={view} onValueChange={setView} items={tabs}
          className={tabs.filter(tab => tab.count === 0).map(tab => `appraisal-tabs-zero-${tab.value}`).join(' ')} />
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">{fees.announcement}</div>
        <div role="tabpanel" id={`appraisal-panel-${view}`} aria-labelledby={`appraisal-tab-${view}`} className="appraisal-report-panel">
          {view === 'payments' ? <PaymentLinksTab report={paymentReport} error={paymentsError} loading={paymentsLoading} isAdmin={isAdmin} search={filters.payments.search}
            page={filters.payments.page} onFilter={updateFilter} onRetry={() => void loadPayments()} onReload={loadPayments} /> : <>
          <p className="appraisal-report-description">{view === 'fees' ? 'Appraisal invoices that may be higher than the fee last disclosed on the CD, or on the LE if there’s no CD yet.' : view === 'ordering' ? 'When an appraisal is added to a loan’s Appraisal folder, we remind the processor to order the CDA. Other files need no action.' : 'Appraisal documents emailed to borrowers, plus any that were skipped or need review.'}</p>
          {view === 'fees' && fees.report?.truncated && <p role="status">Showing the first 500 findings, with confirmed overages first. Totals cover only the rows shown.</p>}
          {view !== 'fees' && truncated.length > 0 && <p role="status">Showing the most recent 2,000 rows per queue. Narrow the date range to see everything.</p>}
          <LwTable presentation="canonical" density="compact" ariaLabel={config.table} scrollRegionLabel={view === 'fees' ? 'Scroll appraisal fee discrepancies' : `Scroll ${config.table.toLowerCase()}`} toolbar={
            <div className="appraisal-grid-toolbar">
              <LwSearchInput value={current.search} onChange={search => updateFilter({ search })} ariaLabel="Search loan, borrower, or document" placeholder="Search loan, borrower, or document" />
              <div className="appraisal-segments" role="group" aria-label="Filter by status">{(view === 'fees' ? FEE_SEGMENTS : SEGMENTS).map(segment => {
                const count = searched.filter(row => row.state === segment).length;
                return <LwButton key={segment} variant="sm" data-empty={count === 0} aria-pressed={current.segments.includes(segment)} onClick={() => updateFilter({ segments: current.segments.includes(segment) ? current.segments.filter(value => value !== segment) : [...current.segments, segment] })}>{segmentLabels[segment]} <span>{count}</span></LwButton>;
              })}</div>
              <span className="appraisal-grid-count">{filtered.length} of {allRows[view].length} {config.unit}{view === 'fees' && ` · ${amount(excess)} confirmed excess`}</span>
            </div>
          } footer={view !== 'fees' && !busy && !error ? <div className="appraisal-grid-footer"><span>{filtered.length ? page * PAGE_SIZE + 1 : 0}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length} {config.unit} · 25 per page</span><div><LwButton variant="sm" data-preserve-disabled disabled={page === 0} onClick={() => updateFilter({ page: page - 1 })}>Previous</LwButton><LwButton variant="sm" data-preserve-disabled disabled={page + 1 === pageCount} onClick={() => updateFilter({ page: page + 1 })}>Next</LwButton></div></div> : undefined}>
            <thead><tr><th scope="col" className="appraisal-chevron"><span className="sr-only">Detail</span></th><th scope="col">Loan</th><th scope="col">{config.title}</th><th scope="col">Status</th><th scope="col">Why</th>{config.columns.map(column => <th key={column.label} scope="col" className={column.right ? 'fee-money' : undefined}>{column.label}</th>)}<th scope="col" className="appraisal-updated">{view === 'fees' ? 'Checked' : 'Updated'}</th></tr></thead>
            <tbody>{!busy && !error && shown.map(row => {
              const key = `${view}:${row.id}`;
              const open = expanded.has(key);
              const detailId = `appraisal-detail-${view}-${row.id}`;
              return <Fragment key={key}>
                <tr className={open ? 'appraisal-grid-row is-expanded' : 'appraisal-grid-row'} onClick={event => {
                  if (!(event.target as HTMLElement).closest('button, a')) {
                    toggle(key);
                  }
                }}>
                  <td className="appraisal-chevron"><LwButton variant="sm" aria-label={`Show detail for ${row.loanNumber}`} aria-expanded={open} aria-controls={detailId} onClick={event => {
                    event.stopPropagation(); toggle(key);
                  }}><ChevronRight size={14} aria-hidden="true" /></LwButton></td>
                  <td className="appraisal-loan"><LoanNumberCell loanNumber={row.loanNumber} /><small>{row.borrowerName || 'Borrower unavailable'}</small></td>
                  <td className="appraisal-document"><strong title={row.title}>{row.title}</strong><small title={row.sub}>{row.sub}</small>{row.fee?.invoiceAmountStatus === 'extracted' && <small className="appraisal-extracted">Extracted · needs verification</small>}</td>
                  <td><LwStatusPill label={row.badge || segmentLabels[row.state]} tone={row.tone || TONES[row.state]} />{row.fee?.alertsStoppedAt && <LwStatusPill label="Alerts stopped" tone="neutral" />}</td>
                  <td className="appraisal-why"><p title={row.why}>{row.why}</p></td>
                  {row.values.map((value, index) => <td key={config.columns[index].label} className={config.columns[index].right ? 'fee-money' : undefined}>{value}</td>)}
                  <td className="appraisal-updated" onClick={event => event.stopPropagation()}>{row.updated}</td>
                </tr>
                <tr id={detailId} hidden={!open} className="appraisal-expansion"><td colSpan={6 + config.columns.length}>{open && <div className="appraisal-detail">
                  <div className="appraisal-detail-lead"><p>{row.why}</p>{row.followUp && <div>{row.followUp}</div>}</div>{row.details}<div className="appraisal-detail-actions" onClick={event => event.stopPropagation()}>{row.actions}</div>
                </div>}</td></tr>
              </Fragment>;
            })}</tbody>
          </LwTable>
          {busy && <div role="status" className="appraisal-report-empty"><Loader2 size={20} aria-hidden="true" />{view === 'fees' ? 'Loading fee compliance findings…' : 'Loading appraisal delivery activity…'}</div>}
          {error && <div role="alert" className="appraisal-report-error"><p>{view === 'fees' ? error : 'Appraisal delivery activity could not be loaded.'}</p>{view !== 'fees' && <p>{error}</p>}<LwButton variant="sm" onClick={() => void (view === 'fees' ? fees.refreshReport() : loadRows())}>Try again</LwButton></div>}
          {!busy && !error && !filtered.length && <LwEmpty title={view === 'fees' ? 'No fee discrepancies to show' : view === 'deliveries' ? 'No completed documents match' : current.segments.length === 1 && current.segments[0] === 'needs' ? 'Nothing needs you' : 'No rows match'}>{view === 'fees' ? 'No saved overages, review findings or invoices awaiting disclosure are available for Active loans you can access.' : view === 'ordering' ? current.segments.length === 1 && current.segments[0] === 'needs' ? 'No CDA-ordering loan in this view needs action.' : 'Widen the date range, or turn on another state above.' : 'Widen the date range or change the filters.'}</LwEmpty>}
          </>}
        </div>
        {fees.tracking && <AppraisalAuditTracker key={fees.tracking.startedAt} run={fees.tracking} onDismiss={fees.dismissTracker} onHistory={() => fees.setHistoryFor(fees.tracking!.row)} />}
        {historyRow && <AppraisalFeeHistoryDrawer subject={historyRow} onClose={() => fees.setHistoryFor(null)}
          onOpenAppraisal={() => void openAppraisal(historyRow)} appraisalBusy={openingAppraisals.includes(historyRow.id)} appraisalUnavailable={!historyRow.appraisalReportId}
          onOpenInvoice={() => void openInvoice(historyRow)} invoiceBusy={openingFiles.includes(historyRow.id)}
          onRerun={() => void fees.rerun(historyRow)} rerunDisabled={fees.rerunInProgress(historyRow)} />}
      </div>
      {stopFor && <StopFeeAlertsDialog loanId={stopFor.loanId || ''} loanNumber={stopFor.loanNumber} onClose={() => setStopFor(null)} onStopped={() => {
        setStopFor(null); void fees.refreshReport();
      }} />}
      <StoredEmailViewer email={storedEmail} open={viewerOpen} onOpenChange={setViewerOpen} />
    </LwThemeScope>
  );
}
function DetailGroup({ title, values }: { title: string; values: string[][] }) {
  return <section className="appraisal-detail-group" data-group={title}><h3>{title}</h3><dl>{values.map(([label, value], index) => <div key={`${label}-${index}`}><dt>{label}</dt><dd data-field={label}>{value}</dd></div>)}</dl></section>;
}
function ActionIcon({ busy, icon = 'open' }: { busy?: boolean; icon?: 'open' | 'rerun' | 'email' | 'bell' }) {
  return <span className="appraisal-action-icon" aria-hidden="true">{busy ? <Loader2 className="animate-spin" size={14} /> : icon === 'rerun' ? <RefreshCw size={14} /> : icon === 'email' ? <Mail size={14} /> : icon === 'bell' ? <BellOff size={14} /> : <ArrowUpRight size={14} />}</span>;
}

/** The Payment links report. A failed reload keeps the rows on screen with the error above them. */
function usePaymentLinksReport() {
  const [report, setReport] = useState<AppraisalPaymentLinksReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  useEffect(() => {
    const counter = generation;
    return () => {
      counter.current++;
    };
  }, []);
  const load = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    setError(null);
    try {
      const next = await getPaymentLinksReport();
      if (generation.current === current) {
        setReport(next);
      }
    } catch (cause) {
      if (generation.current === current) {
        setError(cause instanceof Error ? cause.message : 'Payment links could not be loaded.');
      }
    } finally {
      if (generation.current === current) {
        setLoading(false);
      }
    }
  }, []);
  return { report, loading, error, load };
}

/** Each current payment link on a loan that can still be sent a reminder, with the loan's own Resend. */
function PaymentLinksTab({ report, error, loading, isAdmin, search, page, onFilter, onRetry, onReload }: {
  report: AppraisalPaymentLinksReport | null;
  error: string | null;
  loading: boolean;
  isAdmin: boolean;
  search: string;
  page: number;
  onFilter: (patch: { search?: string; page?: number }) => void;
  onRetry: () => void;
  onReload: () => Promise<void>;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [resend, setResend] = useState<{ row: AppraisalPaymentLinksReportRow; trigger: HTMLElement } | null>(null);
  const [adding, setAdding] = useState(false);
  // After Save link, focus moves once the reloaded report has rendered: to the new row's Resend (the next step,
  // sending the borrower the link), or to Add payment link when the reload failed or that row is not on this page.
  // Waiting for that render, not a frame after the reload resolves, keeps a busy browser from looking before the new
  // row exists. The move is a timer queued after the closed dialog's own focus return to its opener, so it lands last.
  const focusAfterSave = useRef<{ requestId: string | null } | null>(null);
  const [savedReloads, setSavedReloads] = useState(0);
  useEffect(() => {
    const pending = focusAfterSave.current;
    if (!pending) {
      return;
    }
    focusAfterSave.current = null;
    const requestId = error ? null : pending.requestId;
    const timer = window.setTimeout(() => {
      const row = requestId && /^[0-9a-f-]{36}$/i.test(requestId) ? root.current?.querySelector<HTMLElement>(`[data-request-id="${requestId}"]`) : null;
      (row ?? root.current?.querySelector<HTMLElement>('[data-payment-add]'))?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [savedReloads, error]);
  // The server lists only sendable loans; the shared rule keeps this tab and the send path in step.
  const rows = (report?.rows || []).filter(row => appraisalEligibility({
    trans_details_loan_number: row.loanNumber, loan_folder: row.loanFolder, funded_date: row.fundedDate
  }).eligible);
  const query = search.trim().toLowerCase();
  const matches = rows.filter(row => [row.loanNumber, row.borrowerName, row.propertyStreet, row.propertyLocality, row.amcName, row.host, row.loanOfficerName]
    .filter(Boolean).join(' ').toLowerCase().includes(query));
  const pageCount = Math.max(1, Math.ceil(matches.length / PAGE_SIZE));
  const current = Math.min(page, pageCount - 1);
  const shown = matches.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);
  const reminderOff = Boolean(report?.enabled && !report.reminderEnabled);
  const saved = (requestId: string | null) => {
    setAdding(false);
    void onReload().then(() => {
      focusAfterSave.current = { requestId };
      setSavedReloads(count => count + 1);
    });
  };
  const closeResend = (trigger: HTMLElement, attempted: boolean) => {
    setResend(null);
    if (!attempted) {
      return;
    }
    // Rows stay mounted while the report reloads, so focus stays on this row's Resend. If the row is gone, keep it in the tab.
    void onReload().then(() => window.requestAnimationFrame(() => {
      if (!trigger.isConnected) {
        root.current?.focus();
      }
    }));
  };
  if (!report) {
    return error
      ? <div role="alert" className="appraisal-report-error"><p>Payment links could not be loaded.</p><p>{error}</p><LwButton variant="sm" onClick={onRetry}>Try again</LwButton></div>
      : <div role="status" className="appraisal-report-empty"><Loader2 size={20} aria-hidden="true" />Loading payment links…</div>;
  }
  if (!report.enabled) {
    return <div className="appraisal-payments-off">
      <LwEmpty title="Payment links are turned off">Captured links and reminders come back when payment links are turned on again.</LwEmpty>
      <LwButton variant="sm" onClick={() => void onReload()} aria-busy={loading}><ActionIcon busy={loading} icon="rerun" />Refresh</LwButton>
    </div>;
  }
  return (
    <div className="appraisal-payments" ref={root} tabIndex={-1}>
      <p className="appraisal-report-description">Appraisal payment links for active loans. Use Resend to email the link to the borrower again.</p>
      {reminderOff && <p role="status" className="appraisal-payments-note">The payment link reminder email is turned off in Email Notifications, so Resend is unavailable.</p>}
      {report.truncated && <p role="status" className="appraisal-payments-note">Showing the newest 500 payment links.</p>}
      {error && <p role="alert" className="appraisal-payments-note appraisal-payments-note--error">{error}</p>}
      <LwTable presentation="canonical" density="compact" ariaLabel="Appraisal payment links" scrollRegionLabel="Scroll appraisal payment links" toolbar={
        <div className="appraisal-grid-toolbar">
          <LwSearchInput value={search} onChange={value => onFilter({ search: value })} ariaLabel="Search loan, borrower, address, AMC, or loan officer" placeholder="Search loan, borrower, address, AMC, or loan officer" />
          <span className="appraisal-grid-count">{matches.length} of {rows.length} links</span>
          <div className="appraisal-payments-actions">
            <LwButton variant="sm" onClick={() => void onReload()} aria-busy={loading}><ActionIcon busy={loading} icon="rerun" />Refresh</LwButton>
            <LwButton variant="sm-solid" data-payment-add onClick={() => setAdding(true)}><Plus size={14} aria-hidden="true" />Add payment link</LwButton>
          </div>
        </div>
      } footer={matches.length ? <div className="appraisal-grid-footer"><span>{current * PAGE_SIZE + 1}–{Math.min((current + 1) * PAGE_SIZE, matches.length)} of {matches.length} links · 25 per page</span><div>
        <LwButton variant="sm" data-preserve-disabled disabled={current === 0} onClick={() => onFilter({ page: current - 1 })}>Previous</LwButton>
        <LwButton variant="sm" data-preserve-disabled disabled={current + 1 === pageCount} onClick={() => onFilter({ page: current + 1 })}>Next</LwButton>
      </div></div> : undefined}>
        <thead><tr><th scope="col">Loan number</th><th scope="col">Property address</th><th scope="col">AMC</th><th scope="col">Loan officer</th><th scope="col">Payment link</th><th scope="col">Resend</th></tr></thead>
        <tbody>{shown.map(row => {
          const blocked = reminderOff || row.reminderOpen;
          return <tr key={row.id}>
            <td className="appraisal-loan"><LoanNumberCell loanNumber={row.loanNumber} /><small>{row.borrowerName || 'Borrower unavailable'}</small></td>
            <td className="appraisal-payment-property">{row.propertyStreet || row.propertyLocality
              ? <><span>{row.propertyStreet || 'Street not recorded'}</span>{row.propertyLocality && <small>{row.propertyLocality}</small>}</>
              : <small>Not recorded</small>}</td>
            <td>{row.amcName || 'Not named'}</td>
            <td>{row.loanOfficerName || 'Not assigned'}</td>
            <td className="appraisal-payment-link">
              <span>{row.host}</span><LwButton variant="sm" aria-label={`Copy payment link for ${row.loanNumber}`} onClick={() => void copyPaymentLink(row)}><Copy size={14} aria-hidden="true" />Copy link</LwButton>
              {(row.reference.fileId || row.reference.orderReferenceId) && <dl className="appraisal-payment-ids">
                {row.reference.fileId && <div><dt>File ID</dt><dd>{row.reference.fileId}</dd></div>}
                {row.reference.orderReferenceId && <div><dt>Order Reference ID</dt><dd>{row.reference.orderReferenceId}</dd></div>}
              </dl>}
            </td>
            <td className="appraisal-payment-resend">
              <LwButton variant="sm-solid" data-request-id={row.id} aria-label={`Resend payment link for ${row.loanNumber}`} aria-disabled={blocked} onClick={event => {
                if (!blocked) {
                  setResend({ row, trigger: event.currentTarget });
                }
              }}>Resend</LwButton>
              <small>{reminderLine(row, appraisalDate)}</small>
            </td>
          </tr>;
        })}</tbody>
      </LwTable>
      {!matches.length && <LwEmpty title={rows.length ? 'No links match' : 'No payment links to show'}>{rows.length ? 'Change the search.' : 'Links appear here when an AMC payment link is captured for an Active loan you can open.'}</LwEmpty>}
      {resend && <ReportResendDialog row={resend.row} trigger={resend.trigger} onClose={attempted => closeResend(resend.trigger, attempted)} />}
      {adding && <ReportAddPaymentLinkModal isAdmin={isAdmin} onClose={() => setAdding(false)} onSaved={saved} onReload={onReload} />}
    </div>
  );
}

/** The loan's own reminder dialog: the same preview, confirmation and server send path as the Appraisal tab. */
function ReportResendDialog({ row, trigger, onClose }: { row: AppraisalPaymentLinksReportRow; trigger: HTMLElement; onClose: (attempted: boolean) => void }) {
  const loan = useMemo(() => ({ loanId: row.loanId, encompassLoanId: row.encompassLoanId }), [row.loanId, row.encompassLoanId]);
  const requests = useMemo(() => [row], [row]);
  const attempted = useRef(false);
  const preview = useCallback((requestId: string) => previewPaymentLinkReminder(loan, requestId), [loan]);
  const send = useCallback(async (input: PaymentLinkReminderInput) => {
    attempted.current = true;
    return await sendPaymentLinkReminder(loan, input);
  }, [loan]);
  return <SendPaymentReminderDialog requests={requests} returnFocusTo={trigger} formatDate={appraisalDate} onClose={() => onClose(attempted.current)} onPreview={preview} onSend={send} />;
}
